import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  User, Trophy, CheckCircle, Clock, ChevronLeft, 
  Camera, Video, Loader2, Heart, Award
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { PresignedImage, PresignedVideo } from '../components/PresignedMedia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserProfilePage() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${username}/submissions`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <User className="w-12 h-12 text-neutral-600 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">User not found</h2>
        <Link to="/sidequests" className="text-red-400 hover:text-red-300">
          Back to Sidequests
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/sidequests" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5" />
          Back to Sidequests
        </Link>

        {/* Profile Header */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">@{profile.username}</h1>
              <p className="text-neutral-400">Sidequest Participant</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Trophy className="w-5 h-5 text-yellow-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{profile.stats?.total_completed || 0}</p>
              <p className="text-xs text-neutral-400">Completed</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{profile.stats?.on_time || 0}</p>
              <p className="text-xs text-neutral-400">On Time</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Heart className="w-5 h-5 text-red-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{profile.total_likes_received || 0}</p>
              <p className="text-xs text-neutral-400">Total Likes</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Clock className="w-5 h-5 text-orange-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{profile.stats?.late || 0}</p>
              <p className="text-xs text-neutral-400">Late</p>
            </div>
          </div>

          {/* Badges */}
          {profile.badges && profile.badges.length > 0 && (
            <div className="pt-4 border-t border-white/10">
              <p className="text-sm text-neutral-400 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Badges Earned
              </p>
              <div className="flex flex-wrap gap-3">
                {profile.badges.map(badge => (
                  <div 
                    key={badge.id} 
                    className="flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full px-4 py-2 border border-yellow-500/30"
                    title={badge.description}
                  >
                    {badge.icon_url ? (
                      <PresignedImage 
                        src={badge.icon_url}
                        alt={badge.name}
                        className="w-6 h-6 rounded-full object-contain"
                      />
                    ) : (
                      <Award className="w-5 h-5 text-yellow-400" />
                    )}
                    <span className="text-sm font-medium text-white">{badge.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submissions */}
        <h2 className="text-xl font-bold text-white mb-4">Submissions ({profile.total})</h2>

        {profile.submissions.length === 0 ? (
          <div className="text-center py-12 bg-[#121212] rounded-2xl border border-white/10">
            <Camera className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <p className="text-neutral-400">No submissions yet</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {profile.submissions.map(sub => (
              <Link 
                key={sub.id}
                to={`/sidequests/${sub.sidequest_id}`}
                className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden hover:border-white/20 transition-all"
              >
                {/* Media */}
                <div className="relative aspect-video bg-black">
                  {sub.media_type === 'video' ? (
                    <PresignedVideo 
                      src={sub.media_urls[0]}
                      className="w-full h-full object-contain"
                      controls={false}
                      preload="metadata"
                    />
                  ) : (
                    <PresignedImage 
                      src={sub.media_urls[0]}
                      alt="Submission"
                      className="w-full h-full object-contain"
                    />
                  )}
                  {sub.is_late && (
                    <Badge className="absolute top-2 left-2 bg-orange-500/80 text-white">
                      Late
                    </Badge>
                  )}
                  <div className="absolute top-2 right-2">
                    {sub.media_type === 'video' ? (
                      <Video className="w-5 h-5 text-white drop-shadow-lg" />
                    ) : (
                      <Camera className="w-5 h-5 text-white drop-shadow-lg" />
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  {sub.caption && (
                    <p className="text-neutral-300 text-sm mb-2 line-clamp-2">{sub.caption}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-neutral-500">
                      {format(parseISO(sub.submitted_at), 'MMM d, yyyy')}
                    </p>
                    {sub.like_count > 0 && (
                      <div className="flex items-center gap-1 text-neutral-400">
                        <Heart className="w-3.5 h-3.5" />
                        <span className="text-xs">{sub.like_count}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
